import _ from 'lodash'
import React from 'react'
import classNames from 'classnames'
import { IconNames } from '@blueprintjs/icons'
import { Classes, Icon } from '@blueprintjs/core'
import { translateString } from 'shared-libs/helpers/utils'
import { convertCanvasToBlob } from 'browser/app/utils/image'
import { FramesManager } from 'shared-libs/components/view/frames-manager'
import { IBarcodeDetector } from 'browser/app/utils/barcode/barcode-detector'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { AutoCaptureTimer } from 'shared-libs/helpers/vision/auto-capture-timer'
import { globalTranslationTable } from 'browser/mobile/util/global-translation-table'
import { BarcodeDetectorFactory } from 'browser/app/utils/barcode/barcode-detector-factory'
import { LoadingSpinner } from 'browser/components/atomic-elements/atoms/loading-spinner/loading-spinner'
import {
  Point,
  Polygon,
  findBestTextEdge,
  calculateEdgeAngle,
  calculateTextPosition,
} from 'shared-libs/helpers/vision/polygon-utils'
import {
  isBarcodeStable,
  BarcodeDetectionResult,
} from 'browser/app/utils/barcode/barcode-result'

const AUTO_CAPTURE_TIMEOUT_MS = 1500

interface IBarcodeScannerProps {
  onClose?: () => void
  onCapture: (barcode: string) => void
  onCaptureImage?: (barcode: string, image: Blob, width: number, height: number) => void
  helpText?: string
  frames?: FramesManager
}

interface IBarcodeScannerState {
  loading?: boolean
  error?: Error
}

type DecodedResult = {
  barcode: string
  image?: CapturedImageBlob
}

type CapturedImageBlob = {
  blob: Blob
  width: number
  height: number
}

export class BarcodeScanner extends React.PureComponent<
  IBarcodeScannerProps,
  IBarcodeScannerState
> {
  private detector: IBarcodeDetector
  private lastResult?: DecodedResult
  private autoCaptureTimer: AutoCaptureTimer
  private clearOverlayTimeout?: ReturnType<typeof setTimeout>
  private videoRef: React.RefObject<HTMLVideoElement> = React.createRef<HTMLVideoElement>()
  private overlayRef: React.RefObject<SVGSVGElement> = React.createRef<SVGSVGElement>()

  private translationTable: Record<string, any>
  private translatedHelpText: string
  private translatedMoveCloser: string
  private translatedHoldSteady: string

  constructor(props: IBarcodeScannerProps) {
    super(props)

    this.translationTable = _.merge(
      {},
      globalTranslationTable,
      props.frames?.getContext('translationTable')
    )
    this.translatedHelpText = translateString(
      props.helpText || 'Barcode_Field_Default_Help_Text',
      this.translationTable
    )
    this.translatedMoveCloser = translateString('Barcode_Field_Move_Closer', this.translationTable)
    this.translatedHoldSteady = translateString('Barcode_Field_Hold_Steady', this.translationTable)

    this.autoCaptureTimer = new AutoCaptureTimer(AUTO_CAPTURE_TIMEOUT_MS, () => {
      void this.captureResult()
    })

    this.state = {
      loading: false,
    }
  }

  componentDidMount(): void {
    this.detector = BarcodeDetectorFactory.create()
    this.startDecoding()
  }

  componentWillUnmount(): void {
    this.autoCaptureTimer.destroy()
    this.stopDecoding()
  }

  render() {
    const { onClose } = this.props
    return (
      <div className="flex flex-column tc h-100 pa2">
        <div className="tr mb2">
          <Button onClick={onClose}>
            <Icon icon={IconNames.CROSS} />
          </Button>
        </div>
        <div className="flex-grow flex flex-column justify-center">
          {this.renderState()}
          {this.renderVideo()}
        </div>
      </div>
    )
  }

  private renderState() {
    const { error, loading } = this.state
    if (error) {
      return this.renderError(error)
    } else if (loading) {
      return this.renderLoader()
    } else {
      return null
    }
  }

  private renderVideo() {
    const { loading } = this.state

    return (
      <div
        className={classNames({
          'u-hide': loading /* <video> must be in DOM tree when starting decoding */,
        })}
      >
        <div className="barcode-scanner-modal">
          <video id="video" ref={this.videoRef} width="90%" />
          <svg className="barcode-scanner-overlay" id="barcode-overlay" ref={this.overlayRef} />
          {this.renderViewFinder()}
        </div>
        {!_.isEmpty(this.translatedHelpText) && (
          <div className="tc mh4">{this.translatedHelpText}</div>
        )}
      </div>
    )
  }

  private renderViewFinder() {
    return (
      <svg className="barcode-scanner-viewfinder" viewBox="0 0 100 100">
        <path
          /** Draws a unified crosshair shape using percentages. */
          d="M 15,49.5 L 49.5,49.5 L 49.5,40 L 50.5,40 L 50.5,49.5 L 85,49.5 L 85,50.5 L 50.5,50.5 L 50.5,60 L 49.5,60 L 49.5,50.5 L 15,50.5 Z"
          fill="rgba(255, 255, 255, 0.3)"
          filter="url(#softShadow)"
        />
        <defs>
          <filter id="softShadow">
            <feDropShadow dx="0" dy="0" stdDeviation="1" floodColor="rgba(0,0,0,0.3)" />
          </filter>
        </defs>
      </svg>
    )
  }

  private renderLoader() {
    return <LoadingSpinner style={{ pointerEvents: 'none' }} />
  }

  private renderError(error: Error) {
    if (!error) {
      return null
    }

    const translatedErrorTitle = translateString('Error', globalTranslationTable)
    const translatedErrorRetry = translateString('Retry', globalTranslationTable)

    return (
      <div className="mh4">
        <p className="c-lead mb2">{translatedErrorTitle}</p>
        {this.renderErrorBody(error)}
        <Button
          className={classNames('mobile-button mt3 w-100', Classes.BUTTON)}
          onClick={this.startDecoding.bind(this)}
        >
          {translatedErrorRetry}
        </Button>
      </div>
    )
  }

  private renderErrorBody(error: Error) {
    const translatedErrorBody = translateString(
      'Barcode_Field_Error_No_Camera_Permission',
      globalTranslationTable
    )
    const translatedErrorRemedy = translateString(
      'Barcode_Field_Error_No_Camera_Permission_Remedy',
      globalTranslationTable,
      {
        host: window.location.hostname,
      }
    )

    if (error.name === 'NotAllowedError') {
      return (
        <>
          <p>{translatedErrorBody}</p>
          <br />
          <p>{translatedErrorRemedy}</p>
        </>
      )
    } else {
      return <p>{error.message}</p>
    }
  }

  private startDecoding() {
    const videoElement = this.videoRef.current

    this.setState({ loading: true })

    this.detector
      .detect(videoElement, this.handleResult, this.handleError)
      .then(() => {
        // detection has started
      })
      .catch(this.handleError)
      .finally(() => {
        this.setState({ loading: false })
      })
  }

  /** Capture the latest primary barcode, and draw overlays for all barcodes. */
  private handleResult = async (results: BarcodeDetectionResult[]) => {
    if (results.length === 0) {
      return
    }

    this.cancelErase()
    this.drawOverlay(results)
    this.scheduleErase()

    const result = results.find((result) => result.isPrimary)
    if (!result) {
      return
    }

    this.lastResult = result

    if (isBarcodeStable(result)) {
      this.autoCaptureTimer.schedule()
    } else {
      this.autoCaptureTimer.cancel()
    }
  }

  private handleError = (error: unknown) => {
    console.error('Barcode detection error:', error)
    this.autoCaptureTimer.cancel()
    this.setState({
      error: error instanceof Error ? error : new Error(String(error)),
      loading: false,
    })
  }

  private scheduleErase() {
    this.clearOverlayTimeout = setTimeout(() => {
      if (this.overlayRef.current) {
        this.overlayRef.current.innerHTML = ''
      }
    }, 500)
  }

  private cancelErase() {
    clearTimeout(this.clearOverlayTimeout)
  }

  private async captureResult() {
    const { onCapture, onCaptureImage, onClose } = this.props

    const { barcode } = this.lastResult

    if (onCaptureImage) {
      const image = await this.captureImage()
      onCaptureImage(barcode, image.blob, image.width, image.height)
    }

    onCapture(barcode)

    this.stopDecoding()

    onClose?.()
  }

  /** Captures the current video frame as an image */
  private async captureImage(): Promise<DecodedResult['image']> {
    const videoElement = this.videoRef.current

    const canvasElement = document.createElement('canvas')
    const context = canvasElement.getContext('2d')
    canvasElement.width = videoElement.videoWidth
    canvasElement.height = videoElement.videoHeight
    context.drawImage(videoElement, 0, 0, videoElement.videoWidth, videoElement.videoHeight)

    return convertCanvasToBlob(canvasElement, 'image/webp', 0.8).then((blob) => ({
      blob,
      width: videoElement.videoWidth,
      height: videoElement.videoHeight,
    }))
  }

  private drawOverlay(results: BarcodeDetectionResult[]) {
    const svg = this.overlayRef.current
    const videoElement = this.videoRef.current
    if (!svg || !videoElement) {
      return
    }

    const videoRect = videoElement.getBoundingClientRect()
    const scaleX = videoRect.width / videoElement.videoWidth
    const scaleY = videoRect.height / videoElement.videoHeight

    // clear previous overlay state
    svg.innerHTML = ''

    results.forEach((result) => {
      const { cornerPoints, barcode } = result

      const scaledCornerPoints = cornerPoints.map((point) => ({
        x: point.x * scaleX,
        y: point.y * scaleY,
      }))

      const polygon = createPolygon(scaledCornerPoints, result)

      const { primary: edge, opposite: bottomEdge } = findBestTextEdge(scaledCornerPoints)
      const angle = calculateEdgeAngle(edge)
      const point = calculateTextPosition(edge.start, angle)

      const angleDegrees = angle * (180 / Math.PI)
      const textBg = createTextBackground(barcode, point, angleDegrees)
      const text = createText(barcode, point, angleDegrees)

      svg.appendChild(polygon)
      svg.appendChild(textBg)
      svg.appendChild(text)

      if (result.isPrimary && !isBarcodeStable(result)) {
        const advice =
          result.isNear === false ? this.translatedMoveCloser : this.translatedHoldSteady

        const bottomAngle = calculateEdgeAngle(bottomEdge)
        const bottomPoint = calculateTextPosition(bottomEdge.start, bottomAngle, -10)
        const bottomAngleDegrees = bottomAngle * (180 / Math.PI)
        const bottomTextBg = createTextBackground(advice, bottomPoint, bottomAngleDegrees)
        const bottomText = createText(advice, bottomPoint, bottomAngleDegrees)

        svg.appendChild(bottomTextBg)
        svg.appendChild(bottomText)
      }
    })
  }

  private stopDecoding() {
    this.detector?.stop()
  }
}

const PRIMARY_STROKE = 'rgba(248, 153, 57, 1.0)'
const SECONDARY_STROKE = 'rgba(248, 153, 57, 0.3)'
const FOCUSED_FILL = 'rgba(248, 153, 57, 0.15)'
const SECONDARY_FILL = 'none'

export function createPolygon(cornerPoints: Polygon, result: BarcodeDetectionResult) {
  const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon')
  const points = cornerPoints.map((point) => `${point.x},${point.y}`).join(' ')

  const isSatisfactory = result.isPrimary && isBarcodeStable(result)
  const strokeColor = result.isPrimary ? PRIMARY_STROKE : SECONDARY_STROKE
  const fillColor = isSatisfactory ? FOCUSED_FILL : SECONDARY_FILL

  polygon.setAttribute('points', points)
  polygon.setAttribute('fill', fillColor)
  polygon.setAttribute('stroke-width', '2')
  polygon.setAttribute('stroke', strokeColor)

  return polygon
}

export function createTextBackground(
  barcode: string,
  point: Point,
  angleDegrees: number
): SVGRectElement {
  const textBg = document.createElementNS('http://www.w3.org/2000/svg', 'rect')

  const textWidth = barcode.length * 8
  const textHeight = 16

  textBg.setAttribute('x', (point.x - 2).toString())
  textBg.setAttribute('y', (point.y - textHeight / 2 - 1).toString())
  textBg.setAttribute('width', (textWidth + 8).toString())
  textBg.setAttribute('height', (textHeight + 2).toString())
  textBg.setAttribute('fill', 'rgba(0, 0, 0, 0.7)')
  textBg.setAttribute('rx', '3')
  textBg.setAttribute('transform', `rotate(${angleDegrees}, ${point.x}, ${point.y})`)

  return textBg
}

export function createText(content: string, point: Point, angleDegrees: number): SVGTextElement {
  const text = document.createElementNS('http://www.w3.org/2000/svg', 'text')

  text.textContent = content
  text.setAttribute('x', point.x.toString())
  text.setAttribute('y', point.y.toString())
  text.setAttribute('fill', 'white')
  text.setAttribute('font-size', '14')
  text.setAttribute('font-family', 'monospace')
  text.setAttribute('font-weight', 'bold')
  text.setAttribute('dominant-baseline', 'middle')
  text.setAttribute('transform', `rotate(${angleDegrees}, ${point.x}, ${point.y})`)

  return text
}

